import { supabase } from "@/lib/supabase";
import { isStaffAssignedToLocation } from "@/lib/cleanerAssignedLocations";
import { isAssignmentPlaceholderSection } from "@/lib/locationAccess";
import type { TaskFilterContext } from "@/lib/task-filters";
import { calculateTaskProgress } from "@/lib/task-progress";
import {
  resolveStaffAccessCodeDisplay,
  type CommunityEmbedForAccess,
} from "@/lib/resolveLocationAccessCode";
import type { PropertyDetail } from "@/components/cleaner/PropertyDetailSheet";
import { isPlainObject } from "@/lib/checklistJson";

function parseValidationConfig(value: unknown): PropertyDetail["validation_config"] {
  if (!isPlainObject(value)) return null;
  return {
    qr_required: value.qr_required === true,
    gps_required: value.gps_required === true,
    biometric_required: value.biometric_required === true,
  };
}

export async function fetchCleanerPropertyDetail(
  propertyId: string,
  orgId: string
): Promise<PropertyDetail> {
  const { data: locationData, error: locationError } = await supabase
    .from("cleaning_locations")
    .select(
      `
            id,
            address,
            status,
            instruction_notes,
            access_code,
            access_codes,
            community_id,
            community:communities (
              legal_name,
              board_email,
              access_codes,
              operational_notes
            ),
            qr_code_token,
            location_master_id,
            validation_config,
            latitude,
            longitude,
            cleaning_clients (
              name
            )
          `
    )
    .eq("id", propertyId)
    .eq("org_id", orgId)
    .eq("is_cleaning_active", true)
    .single();

  if (locationError || !locationData) {
    throw new Error(locationError?.message ?? "Brak danych budynku.");
  }

  const loc = locationData as Record<string, unknown>;
  const rawCommunity = loc.community;
  const communityEmbed =
    rawCommunity == null
      ? null
      : Array.isArray(rawCommunity)
        ? rawCommunity[0]
        : rawCommunity;

  const resolvedAccessCode = resolveStaffAccessCodeDisplay({
    community: (communityEmbed as CommunityEmbedForAccess) ?? undefined,
    access_code: typeof loc.access_code === "string" ? loc.access_code : null,
    access_codes: typeof loc.access_codes === "string" ? loc.access_codes : null,
  });

  const { data: userData } = await supabase.auth.getUser();
  const currentUserId = userData.user?.id ?? null;

  if (!currentUserId) {
    throw new Error("Wymagane logowanie.");
  }

  const assignedToLocation = await isStaffAssignedToLocation(currentUserId, propertyId);
  if (!assignedToLocation) {
    throw new Error("Nie masz przypisania do tej nieruchomości.");
  }

  const { data: sectionsData, error: sectionsError } = await supabase
    .from("property_sections")
    .select("id, name, assigned_staff_id, allow_cross_access, sort_order")
    .eq("location_id", propertyId)
    .eq("is_active", true)
    .order("sort_order", { ascending: true });

  if (sectionsError) {
    console.error("[fetchCleanerPropertyDetail] Błąd pobierania sekcji:", sectionsError);
  }

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);
  const filterContext: TaskFilterContext = { todayStart, todayEnd };

  const thirtyDaysAgo = new Date(todayStart);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const thirtyDaysAhead = new Date(todayEnd);
  thirtyDaysAhead.setDate(thirtyDaysAhead.getDate() + 31);
  const thirtyDaysAgoISO = thirtyDaysAgo.toISOString();
  const thirtyDaysAheadISO = thirtyDaysAhead.toISOString();

  const locSections = (sectionsData || [])
    .filter((s) => !isAssignmentPlaceholderSection(s.name))
    .map((s) => ({ id: s.id }));
  const sectionsByLocation = new Map<string, Array<{ id: string }>>();
  sectionsByLocation.set(
    propertyId,
    locSections.length > 0 ? locSections : [{ id: `default-${propertyId}` }]
  );

  const { data: tasksData, error: tasksError } = await supabase
    .from("cleaning_tasks")
    .select(
      `
            id,
            location_id,
            section_id,
            status,
            task_type,
            checklist,
            completed_at,
            scheduled_at,
            check_in_metadata
          `
    )
    .eq("location_id", propertyId)
    .eq("org_id", orgId)
    .neq("status", "cancelled")
    .or(
      `and(scheduled_at.gte.${thirtyDaysAgoISO},scheduled_at.lte.${thirtyDaysAheadISO}),task_type.eq.coordinator_single,task_type.eq.employee_extra,scheduled_at.is.null`
    );

  if (tasksError) {
    console.error("[fetchCleanerPropertyDetail] Błąd pobierania zadań sekcji:", tasksError);
  }

  const taskProgress = calculateTaskProgress(
    Array.isArray(tasksData) ? tasksData : [],
    sectionsByLocation,
    filterContext
  );
  const sectionCounters = taskProgress[propertyId]?.sections ?? {};

  const mappedSections = (sectionsData || [])
    .filter((section) => !isAssignmentPlaceholderSection(section.name))
    .map((section) => {
    const counters = sectionCounters[section.id] ?? { total: 0, completed: 0 };
    return {
      id: section.id,
      name: section.name,
      assignedTo: undefined,
      isAssignedToMe:
        !section.assigned_staff_id || section.assigned_staff_id === currentUserId,
      allowCrossAccess: section.allow_cross_access ?? true,
      tasksTotal: counters.total,
      tasksCompleted: counters.completed,
      checklistTasks: [],
      inventory: [],
      activityLog: [],
      instructionNotes: locationData.instruction_notes || undefined,
      accessCode: resolvedAccessCode ?? undefined,
    };
  });

  const totalTasks = mappedSections.reduce((sum, section) => sum + section.tasksTotal, 0);
  const completedTasks = mappedSections.reduce((sum, section) => sum + section.tasksCompleted, 0);

  let priority: PropertyDetail["priority"] = "low";
  if (totalTasks > 0 && completedTasks === totalTasks) {
    priority = "low";
  } else if (completedTasks > 0) {
    priority = "medium";
  } else if (totalTasks > 0) {
    priority = "high";
  }

  const statusRaw = locationData.status;
  const status: PropertyDetail["status"] =
    statusRaw === "completed" || statusRaw === "in_progress" || statusRaw === "pending"
      ? statusRaw
      : "pending";

  return {
    id: locationData.id,
    address: locationData.address,
    clientName: Array.isArray(locationData.cleaning_clients)
      ? locationData.cleaning_clients[0]?.name
      : locationData.cleaning_clients?.name,
    priority,
    status,
    sections: mappedSections,
    inventory: [],
    coordinatorNotes: locationData.instruction_notes || undefined,
    qr_code_token: locationData.qr_code_token,
    location_master_id: locationData.location_master_id,
    checklist: null,
    validation_config: parseValidationConfig(locationData.validation_config),
    latitude: locationData.latitude,
    longitude: locationData.longitude,
  };
}
